import {produce} from 'immer';
import {useAtom} from 'jotai';
import {memo, useEffect} from 'react';
import {AnalysisItem} from '@/map/@types';
import {useQueryBasicAnalysis, useRemoveHouseholdRQData} from '@/map/api';
import {anaysisListAtom} from '@/map/atom';
import {HouseMarkerGroup} from '@/map/components/Markers/House';
import {SHAPE_COLOR_OPTIONS} from '@/map/constants';
import {AreaOverlayType} from '@/map/enums';
import {Polygon} from '@/map/modules';
import {useAiPromptStack} from '../AiPrompt/hook';
import {CircleWithCenterPoint, EmdArea} from '../Overlay';
import {AnalysisPanel} from './AnalysisPanel';
import {DeliveryTileLayer} from './AnalysisPanel/Delivery';
import {FootTrafficTileLayer} from './FootTrafficTile';

interface IProps {
  map: naver.maps.Map;
}

interface ILayerProps extends IProps {
  item: AnalysisItem;
  remove?(item: AnalysisItem): void;
}

export const AnalysisLayer = memo((props: IProps) => {
  const [items, setAnalysisList] = useAtom(anaysisListAtom);
  const clearQueryData = useRemoveHouseholdRQData();
  const {removeAiPropmt, clearAiPrompt} = useAiPromptStack();

  const remove = (item: AnalysisItem) => {
    if (item?.id) {
      setAnalysisList(
        produce((draft) => {
          const index = draft.findIndex((d) => d.id === item.id);
          if (index !== -1) draft.splice(index, 1);
        })
      );
      removeAiPropmt(item.id);
      clearQueryData(item.id);
    }
  };

  useEffect(() => {
    return () => {
      setAnalysisList([]);
      clearAiPrompt();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {items.map((item) => (
        <Layer key={item.id} item={item} remove={remove} {...props} />
      ))}
    </>
  );
});

const Layer = ({map, item, remove}: ILayerProps) => {
  const {data} = useQueryBasicAnalysis(item);

  if (!item?.id) {
    return null;
  }

  const handleRemove = () => remove?.(item);

  return (
    <>
      {item.type === AreaOverlayType.CIRCLE &&
        !!item[AreaOverlayType.CIRCLE]?.center && (
          <CircleWithCenterPoint
            map={map}
            {...item[AreaOverlayType.CIRCLE]}
            {...SHAPE_COLOR_OPTIONS[item.type]}
          />
        )}
      {item.type === AreaOverlayType.POLYGON &&
        !!item[AreaOverlayType.POLYGON]?.paths && (
          <Polygon
            map={map}
            paths={item[AreaOverlayType.POLYGON].paths}
            {...SHAPE_COLOR_OPTIONS[item.type]}
          />
        )}
      {item.type === AreaOverlayType.EMD &&
        !!item[AreaOverlayType.EMD]?.code && (
          <EmdArea
            map={map}
            item={item[AreaOverlayType.EMD]}
            colorOptions={SHAPE_COLOR_OPTIONS[item.type]}
          />
        )}
      <FootTrafficTileLayer item={item} map={map} />
      <DeliveryTileLayer item={item} map={map} />
      {!!data && (
        <HouseMarkerGroup
          items={data}
          map={map}
          hidden={item.footTraffic?.showTile}
        />
      )}
      <AnalysisPanel map={map} item={item} remove={handleRemove} />
    </>
  );
};
